import React, { Component, ErrorInfo, ReactNode } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { captureException } from "@/config/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class OnboardingErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to console
    console.error(
      "Onboarding Error Boundary caught an error:",
      error,
      errorInfo,
    );

    this.setState({
      error,
      errorInfo,
    });

    // Send to Sentry with additional context
    captureException(error, {
      errorBoundary: "OnboardingErrorBoundary",
      componentStack: errorInfo.componentStack,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
          <div className="container mx-auto px-4 max-w-2xl">
            <Card className="border-red-200">
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-red-900">
                  Oops! Something went wrong
                </CardTitle>
                <CardDescription className="text-lg text-red-700">
                  We encountered an unexpected error during the onboarding
                  process.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-900 mb-2">
                    Error Details:
                  </h4>
                  <p className="text-sm text-red-700 font-mono">
                    {this.state.error?.message || "Unknown error occurred"}
                  </p>
                </div>

                <div className="text-sm text-gray-600">
                  <p className="mb-2">You can try the following:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Click "Try Again" to reset the onboarding process</li>
                    <li>Refresh the page to start over</li>
                    <li>Contact support if the problem persists</li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button onClick={this.handleReset} className="flex-1">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    onClick={this.handleReload}
                    variant="outline"
                    className="flex-1"
                  >
                    Refresh Page
                  </Button>
                </div>

                {process.env.NODE_ENV === "development" &&
                  this.state.errorInfo && (
                    <details className="mt-6">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                        Developer Details (Development Only)
                      </summary>
                      <div className="mt-2 p-4 bg-gray-100 rounded-lg">
                        <pre className="text-xs text-gray-800 whitespace-pre-wrap overflow-auto">
                          {this.state.error?.stack}
                          {"\n\nComponent Stack:"}
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    </details>
                  )}
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
